import {
	ArrowLeft,
	Camera,
	Check,
	ClipboardCheck,
	Clock,
	FileText,
	Flag,
	FolderOpen,
	History,
	LayoutDashboard,
	Lightbulb,
	ListChecks,
	type LucideIcon,
	MessagesSquare,
	Network,
	PanelLeftClose,
	PanelLeftOpen,
	Stethoscope,
	Tags,
	User,
	Users,
} from 'lucide-react'
import { useState } from 'react'
import {
	Navigate,
	NavLink,
	Outlet,
	useLocation,
	useNavigate,
	useParams,
} from 'react-router'
import { Button } from '@/components/occurrences/button'
import {
	NextStepBar,
	StageStepper,
} from '@/components/occurrences/occurrence-flow'
import {
	JustificationDialog,
	Notice,
	StatusBadge,
} from '@/components/occurrences/occurrence-ui'
import { PrototypeBar } from '@/components/occurrences/prototype-bar'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/lib/utils'
import { PERSONA } from '@/mocks/occurrences/catalog'
import {
	can,
	canSeeOccurrence,
	getTransitions,
	isLockedAfterValidation,
	progressPercent,
	progressStages,
	SECTIONS,
	type SectionKey,
	type Transition,
	visibleSections,
} from '@/mocks/occurrences/rules'
import { useOccurrenceMock } from '@/mocks/occurrences/store'
import { TYPE_LABEL } from '@/types/occurrence'
import { AccessDenied } from './access-denied'
import type { OccurrenceContext } from './occurrence-context'

const SECTION_PATH: Record<SectionKey, string> = {
	summary: '',
	data: 'data',
	employee: 'employee',
	classification: 'classification',
	evidence: 'evidence',
	testimonies: 'testimonies',
	committee: 'committee',
	timeline: 'timeline',
	investigation: 'investigation',
	matrix: 'matrix',
	actions: 'actions',
	lesson: 'lesson',
	medical: 'medical',
	documents: 'documents',
	closure: 'closure',
	audit: 'audit',
}

const SECTION_ICON: Record<SectionKey, LucideIcon> = {
	summary: LayoutDashboard,
	data: FileText,
	employee: User,
	classification: Tags,
	evidence: Camera,
	testimonies: MessagesSquare,
	committee: Users,
	timeline: Clock,
	investigation: Network,
	matrix: ListChecks,
	actions: ClipboardCheck,
	lesson: Lightbulb,
	medical: Stethoscope,
	documents: FolderOpen,
	closure: Flag,
	audit: History,
}

const NAV_KEY = 'ssma-occurrence-nav-collapsed'
const readCollapsed = () => {
	try {
		return localStorage.getItem(NAV_KEY) === '1'
	} catch {
		return false
	}
}

export const OccurrenceLayout = () => {
	const { id } = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const { occurrences, profile, transition } = useOccurrenceMock()
	const userName = PERSONA[profile].name
	const occ = occurrences.find((o) => o.id === id)

	const [justFor, setJustFor] = useState<Transition | null>(null)
	const [collapsed, setCollapsed] = useState(readCollapsed)
	const toggleNav = () =>
		setCollapsed((c) => {
			try {
				localStorage.setItem(NAV_KEY, c ? '0' : '1')
			} catch {
				// preferência só desta sessão
			}
			return !c
		})

	if (!occ) return <AccessDenied message="Ocorrência não encontrada." />
	if (!canSeeOccurrence(profile, occ, userName))
		return (
			<AccessDenied message="Você não tem acesso a esta ocorrência (escopo, impacto médico ou compartilhamento)." />
		)

	const visible = visibleSections(occ, profile)
	const base = `/occurrences/${occ.id}`
	const current = location.pathname
		.replace(base, '')
		.replace(/^\//, '')
		.split('/')[0]
	const currentKey = (Object.keys(SECTION_PATH) as SectionKey[]).find(
		(k) => SECTION_PATH[k] === current,
	)
	if (currentKey && !visible.includes(currentKey))
		return <Navigate replace to={base} />

	const closedLike = occ.status === 'CLOSED' || occ.status === 'CANCELLED'
	const ctx: OccurrenceContext = {
		occ,
		profile,
		userName,
		editable: can(profile, 'edit') && !closedLike,
		locked: isLockedAfterValidation(occ),
	}

	const stages = progressStages(occ)
	const sectionDone = (k: SectionKey) => {
		const st = stages.filter((s) => s.section === k && s.applicable)
		return st.length > 0 && st.every((s) => s.done)
	}
	const transitions = getTransitions(occ, profile)
	const nextStep = transitions.find((t) => t.variant === 'primary')
	const secondary = transitions.filter(
		(t) => t !== nextStep && !t.deniedByProfile,
	)
	const goTo = (k: SectionKey) =>
		navigate(`${base}${SECTION_PATH[k] ? `/${SECTION_PATH[k]}` : ''}`)
	const groups = Array.from(new Set(SECTIONS.map((s) => s.group)))

	const runTransition = (t: Transition) => {
		if (!t.allowed) return
		if (t.requiresJustification) return setJustFor(t)
		transition(occ.id, t.to)
	}

	return (
		<div className="flex flex-1 flex-col">
			<PrototypeBar />

			{/* Cabeçalho da ocorrência */}
			<div className="border-b bg-card px-4 py-3 sm:px-6">
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div className="flex min-w-0 items-start gap-3">
						<Button
							variant="ghost"
							size="icon"
							className="size-9 shrink-0"
							onClick={() => navigate(ROUTES.OCCURRENCES.INDEX)}
						>
							<ArrowLeft className="size-5" />
						</Button>
						<div className="min-w-0">
							<div className="flex flex-wrap items-center gap-2">
								<h1 className="text-xl font-semibold text-kaefer-dark-grey sm:text-2xl">
									Ocorrência #{occ.number}
								</h1>
								<StatusBadge status={occ.status} />
							</div>
							<p className="truncate text-sm text-muted-foreground">
								{TYPE_LABEL[occ.type]} · {occ.branch || 'Sem filial'}
								{occ.employee && ` · ${occ.employee.name}`}
							</p>
						</div>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						{secondary.map((t) => (
							<Button
								key={t.to + t.label}
								size="sm"
								variant={t.variant === 'danger' ? 'destructive' : 'outline'}
								disabled={!t.allowed}
								onClick={() => runTransition(t)}
								title={t.blockers.map((b) => b.text).join(' · ')}
							>
								{t.label}
							</Button>
						))}
					</div>
				</div>

				{/* Etapas da ocorrência */}
				<StageStepper
					stages={stages.filter((x) => x.applicable)}
					percent={progressPercent(occ)}
					onGo={goTo}
				/>
			</div>

			<div className="flex flex-1 flex-col lg:flex-row">
				{/* Navegação: só módulos aplicáveis ao tipo e ao perfil (recolhível no desktop) */}
				<nav
					className={cn(
						'shrink-0 border-b bg-card/50 transition-[width] lg:sticky lg:top-0 lg:max-h-screen lg:self-start lg:overflow-y-auto lg:border-r lg:border-b-0',
						collapsed ? 'lg:w-[68px]' : 'lg:w-60',
					)}
				>
					<div className="hidden px-3 pt-3 lg:block">
						<Button
							variant="ghost"
							onClick={toggleNav}
							title={collapsed ? 'Expandir menu' : 'Recolher menu'}
							aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
							className={cn(
								'h-9 w-full text-xs text-muted-foreground hover:text-kaefer-dark-grey',
								collapsed ? 'justify-center px-0' : 'justify-start px-2.5',
							)}
						>
							{collapsed ? (
								<PanelLeftOpen className="size-5" />
							) : (
								<>
									<PanelLeftClose className="size-4" /> Recolher menu
								</>
							)}
						</Button>
					</div>
					<div className="flex gap-1 overflow-x-auto p-2 lg:flex-col lg:overflow-visible lg:p-3 lg:pt-1">
						{groups.map((g, gi) => {
							const items = SECTIONS.filter(
								(s) => s.group === g && visible.includes(s.key),
							)
							if (items.length === 0) return null
							return (
								<div key={g} className="flex gap-1 lg:flex-col">
									{collapsed ? (
										gi > 0 && (
											<span className="mx-2 my-1.5 hidden border-t lg:block" />
										)
									) : (
										<p className="hidden px-2 pt-2 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase lg:block">
											{g}
										</p>
									)}
									{items.map((s) => {
										const Icon = SECTION_ICON[s.key]
										const done = sectionDone(s.key)
										return (
											<NavLink
												key={s.key}
												end={s.key === 'summary'}
												title={collapsed ? s.label : undefined}
												to={`${base}${SECTION_PATH[s.key] ? `/${SECTION_PATH[s.key]}` : ''}`}
												className={({ isActive }) =>
													cn(
														'relative flex shrink-0 items-center gap-2.5 rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors',
														collapsed && 'lg:justify-center lg:px-0',
														isActive
															? 'bg-kaefer-dark-grey text-white'
															: 'text-kaefer-dark-grey hover:bg-muted',
													)
												}
											>
												<Icon className="size-4 shrink-0" />
												<span
													className={cn('flex-1', collapsed && 'lg:hidden')}
												>
													{s.label}
												</span>
												{done &&
													(collapsed ? (
														<span className="absolute top-1 right-1 hidden size-2 rounded-full bg-kaefer-grass-green lg:block" />
													) : (
														<Check className="size-3.5 text-kaefer-grass-green" />
													))}
											</NavLink>
										)
									})}
								</div>
							)
						})}
					</div>
				</nav>

				<main className="min-w-0 flex-1 space-y-4 p-4 sm:p-6">
					{nextStep && !closedLike && (
						<NextStepBar
							transition={nextStep}
							profile={profile}
							onGo={goTo}
							onRun={runTransition}
						/>
					)}
					{occ.status === 'CANCELLED' && (
						<Notice tone="lock">
							Ocorrência cancelada. Justificativa: {occ.cancelledReason}
						</Notice>
					)}
					<Outlet context={ctx} />
				</main>
			</div>

			<JustificationDialog
				open={Boolean(justFor)}
				onOpenChange={(v) => !v && setJustFor(null)}
				title={justFor?.label ?? ''}
				danger={justFor?.variant === 'danger'}
				confirmLabel={justFor?.label}
				onConfirm={(text) => justFor && transition(occ.id, justFor.to, text)}
			/>
		</div>
	)
}
